import { DOCUMENT, inject, Injectable } from '@angular/core';
import { type PortalThemeColors } from '@sds/api/administration/models';
import { Theme } from '@sds/shared/enums';
import { type Color } from '@sds/shared/models';
import { computeColors } from '@sds/shared/utils';

type PaletteType = 'primary' | 'accent' | 'warn' | 'success' | 'alert';

@Injectable()
export class DynamicStylerService {
  private readonly document: Document = inject(DOCUMENT);

  attachThemeStyles(): void {
    this.document.body.classList.add('mat-typography', 'mat-app-background');
  }

  applyTheme(theme: Theme, preferredColorScheme: 'dark' | 'light'): void {
    const themes: string[] = Object.values(Theme);
    themes.forEach(item => {
      this.document.body.classList.remove(item);
    });

    if (theme === Theme.System) {
      this.document.body.classList.add(preferredColorScheme === 'dark' ? Theme.Dark : Theme.Atomic);
    } else {
      this.document.body.classList.add(theme ?? Theme.Dark);
    }
  }

  applyCssVars(theme: Theme, themeColors: PortalThemeColors): void {
    switch (theme) {
      case Theme.Light:
        this.applyPalette(themeColors.lightPrimaryColor, 'primary');
        this.applyPalette('#000aaa', 'accent');
        this.applyPalette('#dc2626', 'warn');
        this.applyPalette('#16a34a', 'success');
        this.applyPalette('#f59e0b', 'alert');
        break;
      case Theme.Atomic:
        this.applyPalette('#0e60bf', 'primary');
        this.applyPalette('#00a3bf', 'accent');
        this.applyPalette('#dc2626', 'warn');
        this.applyPalette('#16a34a', 'success');
        this.applyPalette('#f59e0b', 'alert');
        break;
      default:
        this.applyPalette(themeColors.darkPrimaryColor, 'primary');
        this.applyPalette('#eeeaaa', 'accent');
        this.applyPalette('#F44336', 'warn');
        this.applyPalette('#16a34a', 'success');
        this.applyPalette('#f59e0b', 'alert');
        break;
    }
  }

  private applyPalette(color: string, palette: PaletteType): void {
    const colors = computeColors(color);
    this.updateTheme(colors, palette);
  }

  private updateTheme(colors: Color[], palette: PaletteType): void {
    colors.forEach(color => {
      this.document.documentElement.style.setProperty(`--palette-${palette}-${color.name}`, color.hex);
      this.document.documentElement.style.setProperty(
        `--palette-${palette}-contrast-${color.name}`,
        color.darkContrast ? 'rgba(0, 0, 0, 0.87)' : 'white'
      );
    });
  }
}
